import React from 'react';
import { motion } from 'framer-motion';
import { MapPin, Phone, Mail, Clock, ExternalLink } from 'lucide-react';
import { staggerContainer, fadeUp, fadeLeft, fadeRight } from '../../animations/variants';

const hours = [
  { day: 'Tuesday – Friday', time: '11:00 AM – 6:00 PM' },
  { day: 'Saturday', time: '10:00 AM – 2:00 PM' },
  { day: 'Sunday', time: 'Closed' },
  { day: 'Monday', time: 'Closed' },
];

export function ContactSection() {
  return (
    <section id="contact" className="py-20 sm:py-28 bg-white">
      <div className="container-max section-padding">
        <motion.div
          variants={staggerContainer}
          initial="hidden"
          whileInView="visible"
          viewport={{ once: true, margin: '-80px' }}
          className="text-center mb-14"
        >
          <motion.p variants={fadeUp} className="text-ndms-blue font-semibold text-sm uppercase tracking-widest mb-3">
            Get in Touch
          </motion.p>
          <motion.h2 variants={fadeUp} className="text-3xl sm:text-4xl font-display font-bold text-ndms-navy mb-4">
            Visit or Contact Us
          </motion.h2>
          <motion.p variants={fadeUp} className="text-gray-600 max-w-xl mx-auto">
            Our knowledgeable staff are ready to help you find the right compression or brace solution.
          </motion.p>
        </motion.div>

        <div className="grid grid-cols-1 lg:grid-cols-2 gap-8">
          {/* Contact info */}
          <motion.div
            variants={fadeLeft}
            initial="hidden"
            whileInView="visible"
            viewport={{ once: true }}
            className="space-y-4"
          >
            <div className="card p-6">
              <h3 className="font-bold text-ndms-navy mb-4">Store Information</h3>
              <div className="space-y-4">
                <div className="flex items-start gap-3">
                  <div className="w-9 h-9 bg-ndms-navy/5 rounded-xl flex items-center justify-center flex-shrink-0">
                    <MapPin size={18} className="text-ndms-navy" />
                  </div>
                  <div>
                    <div className="font-semibold text-gray-800 text-sm">Address</div>
                    <div className="text-gray-600 text-sm mt-0.5">7-510 44 Street East<br />Saskatoon, SK S7K 0W1</div>
                    <a
                      href="https://maps.google.com"
                      target="_blank"
                      rel="noopener noreferrer"
                      className="inline-flex items-center gap-1 text-xs text-ndms-blue mt-1 hover:underline"
                    >
                      Get Directions <ExternalLink size={10} />
                    </a>
                  </div>
                </div>

                <div className="flex items-start gap-3">
                  <div className="w-9 h-9 bg-ndms-navy/5 rounded-xl flex items-center justify-center flex-shrink-0">
                    <Phone size={18} className="text-ndms-navy" />
                  </div>
                  <div>
                    <div className="font-semibold text-gray-800 text-sm">Phone</div>
                    <a href="tel:+13069794115" className="text-ndms-blue text-sm hover:underline mt-0.5 block">
                      +1 (306) 979-4115
                    </a>
                  </div>
                </div>

                <div className="flex items-start gap-3">
                  <div className="w-9 h-9 bg-ndms-navy/5 rounded-xl flex items-center justify-center flex-shrink-0">
                    <Mail size={18} className="text-ndms-navy" />
                  </div>
                  <div>
                    <div className="font-semibold text-gray-800 text-sm">Email</div>
                    <a href="mailto:northdalesask@gmail.com" className="text-ndms-blue text-sm hover:underline mt-0.5 block">
                      northdalesask@gmail.com
                    </a>
                  </div>
                </div>
              </div>
            </div>

            <div className="card p-6">
              <div className="flex items-center gap-2 mb-4">
                <Clock size={18} className="text-ndms-navy" />
                <h3 className="font-bold text-ndms-navy">Store Hours</h3>
              </div>
              <div className="space-y-2.5">
                {hours.map(({ day, time }) => (
                  <div key={day} className="flex items-center justify-between text-sm">
                    <span className="text-gray-600">{day}</span>
                    <span className={`font-semibold ${time === 'Closed' ? 'text-red-500' : 'text-ndms-navy'}`}>
                      {time}
                    </span>
                  </div>
                ))}
              </div>
            </div>
          </motion.div>

          {/* Map placeholder */}
          <motion.div
            variants={fadeRight}
            initial="hidden"
            whileInView="visible"
            viewport={{ once: true }}
            className="card overflow-hidden"
          >
            <div className="h-full min-h-64 bg-gradient-to-br from-ndms-cream to-ndms-warm flex flex-col items-center justify-center gap-4 p-8 text-center">
              <div className="w-16 h-16 bg-ndms-navy/10 rounded-2xl flex items-center justify-center">
                <MapPin size={32} className="text-ndms-navy" />
              </div>
              <div>
                <h3 className="font-bold text-ndms-navy mb-2">Find Us</h3>
                <p className="text-gray-500 text-sm">7-510 44th Street East, Saskatoon, SK S7K 0W1</p>
              </div>
              <a
                href="https://maps.google.com/?q=Northdale+Medical+Supplies"
                target="_blank"
                rel="noopener noreferrer"
                className="btn-primary text-sm py-2.5 px-5"
              >
                Open in Google Maps
              </a>
            </div>
          </motion.div>
        </div>
      </div>
    </section>
  );
}
